import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import { fetcher, throwApiError } from '@commenda/utils';

import { IDLE_AGENTS_POLL_MS, RUNNING_AGENTS_POLL_MS } from './constants';
import { scheduledAgentsKeys } from './keys';
import { AGENT_NOTIFICATIONS_ROUTE, SCHEDULED_AGENTS_ROUTE } from './routes';
import type {
  IAgentNotificationChannelsResponse,
  IAgentNotificationTargetsResponse,
  IManagedAgentSummary,
  IScheduledAgentDetailResponse,
  IScheduledAgentListResponse,
  IScheduledAgentRunsPage,
  TAgentNotificationChannel,
  TScheduledAgentFacet,
} from './types';

export function useScheduledAgents(
  companyId: number | null,
  facet: TScheduledAgentFacet
) {
  return useQuery({
    queryKey: scheduledAgentsKeys.list(companyId ?? 0, facet),
    queryFn: async () => {
      const res = await fetcher(
        `${SCHEDULED_AGENTS_ROUTE}?companyId=${companyId}&facet=${facet}`,
        { method: 'GET' }
      );
      if (!res.ok) await throwApiError(res, 'Failed to load agents');
      return (await res.json()) as IScheduledAgentListResponse;
    },
    enabled: companyId != null && companyId > 0,
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
    refetchInterval: (query) =>
      query.state.data?.agents.some((agent) => agent.isRunning)
        ? RUNNING_AGENTS_POLL_MS
        : IDLE_AGENTS_POLL_MS,
  });
}

export function useCachedScheduledAgentTitles(companyId: number | null) {
  const queryClient = useQueryClient();

  return useCallback((): string[] => {
    if (companyId === null) return [];

    const cached = queryClient.getQueryData<IScheduledAgentListResponse>(
      scheduledAgentsKeys.list(companyId, 'all')
    );
    return (cached?.agents ?? []).map((agent) => agent.title);
  }, [companyId, queryClient]);
}

export function useScheduledAgent(id: string | null) {
  return useQuery({
    queryKey: scheduledAgentsKeys.detail(id ?? ''),
    queryFn: async () => {
      const res = await fetcher(`${SCHEDULED_AGENTS_ROUTE}/${id}`, {
        method: 'GET',
      });
      if (!res.ok) await throwApiError(res, 'Failed to load agent');
      return (await res.json()) as IScheduledAgentDetailResponse;
    },
    enabled: id != null && id.length > 0,
    refetchOnMount: 'always',
  });
}

export function useScheduledAgentRuns(id: string | null) {
  return useInfiniteQuery({
    queryKey: scheduledAgentsKeys.runs(id ?? ''),
    queryFn: async ({ pageParam }) => {
      const cursorParam = pageParam ? `?cursor=${pageParam}` : '';
      const res = await fetcher(
        `${SCHEDULED_AGENTS_ROUTE}/${id}/runs${cursorParam}`,
        { method: 'GET' }
      );
      if (!res.ok) await throwApiError(res, 'Failed to load runs');
      return (await res.json()) as IScheduledAgentRunsPage;
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: id != null && id.length > 0,
    refetchInterval: (query) => {
      const firstPage = query.state.data?.pages[0];
      const hasActiveRun = (firstPage?.runs ?? []).some(
        (run) => run.completedAt === null && run.skipReason === null
      );
      return hasActiveRun ? 4000 : false;
    },
  });
}

export function useManagedAgents() {
  return useQuery({
    queryKey: scheduledAgentsKeys.managedAgents(),
    queryFn: async () => {
      const res = await fetcher(`${SCHEDULED_AGENTS_ROUTE}/managed-agents`, {
        method: 'GET',
      });
      if (!res.ok) await throwApiError(res, 'Failed to load managed agents');
      return (await res.json()) as { managedAgents: IManagedAgentSummary[] };
    },
  });
}

export function useAgentNotificationChannels(
  agentId?: string,
  isEnabled = true
) {
  return useQuery({
    queryKey: scheduledAgentsKeys.notificationChannels(agentId),
    queryFn: async () => {
      const res = await fetcher(
        `${AGENT_NOTIFICATIONS_ROUTE}/channels${agentId ? `?agentId=${agentId}` : ''}`,
        { method: 'GET' }
      );
      if (!res.ok)
        await throwApiError(res, 'Failed to load notification channels');

      return (await res.json()) as IAgentNotificationChannelsResponse;
    },
    enabled: isEnabled,
  });
}

export function useResumeReconnectedChannels() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetcher(`${AGENT_NOTIFICATIONS_ROUTE}/channels/resume`, { method: 'POST' });
      if (!res.ok) await throwApiError(res, 'Failed to resume notification channels');

      return (await res.json()) as { resumed: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduledAgentsKeys.all });
    },
  });
}

export function useAgentNotificationTargets(
  channel: TAgentNotificationChannel | null,
  agentId?: string,
  search?: string
) {
  return useQuery({
    queryKey: scheduledAgentsKeys.notificationTargets(
      channel ?? 'EMAIL',
      agentId,
      search
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (agentId) params.set('agentId', agentId);
      if (search) params.set('search', search);
      const query = params.toString();

      const res = await fetcher(
        `${AGENT_NOTIFICATIONS_ROUTE}/channels/${channel}/targets${query ? `?${query}` : ''}`,
        { method: 'GET' }
      );
      if (!res.ok)
        await throwApiError(res, 'Failed to load notification destinations');

      return (await res.json()) as IAgentNotificationTargetsResponse;
    },
    enabled: channel !== null,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
